import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Checkbox, FormControlLabel, TextField, MenuItem, Alert, LinearProgress, Chip, Divider,
} from '@mui/material';
import { WarningAmber, Block, HistoryToggleOff } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useCapabilities } from '../contexts/CapabilitiesContext';

const API = process.env.REACT_APP_API_ENDPOINT || '';

/** Above the meter grid and its tiles. Any popover opened INSIDE this dialog must beat it. */
const ACK_DIALOG_Z = 2200;

/** Must stay in step with REASON_CODES in meter_maintenance.py. */
const REASON_CODES = [
  { code: 'prove_scheduled', label: 'Prove scheduled' },
  { code: 'meter_out_of_service', label: 'Meter out of service' },
  { code: 'under_investigation', label: 'Known — under investigation' },
  { code: 'accepted_variance', label: 'Accepted variance' },
  { code: 'other', label: 'Other' },
];

export interface WarningTrack {
  meter_product_id: string;
  label: string;              // e.g. "KER HI"
  health: string;             // current_health (the truth, not display_health)
  reasons: string[];
  acknowledged?: boolean;
}

interface Props {
  open: boolean;
  meterId: string;            // physical meter, e.g. "BAY 11 POS 1"
  tracks: WarningTrack[];
  onClose: () => void;
  onChanged: () => void;      // parent refetches this meter in place — no page reload
  /** Open the history for one track. Without this, an EXPIRED acknowledgement becomes unreachable:
      the card's chip is gone, so nobody can see that the warning was ever parked, or by whom. */
  onViewHistory?: (meterProductId: string) => void;
}

/**
 * Acknowledge warnings on one meter (ACKNOWLEDGE_WARNINGS_PLAN.md decisions 11 & 19).
 *
 * Opened by clicking the ⚠ on a meter card. Lists every warning track so the user can acknowledge one,
 * some or all — 97 warnings sit on only 39 physical meters, so per-track-only would mean clearing the
 * same meter three times.
 *
 * Critical tracks are listed but NOT selectable, with the reason shown. Hiding them would make the rule
 * undiscoverable; a user needs to see WHY that track can't be parked.
 */
const AcknowledgeWarningPanel: React.FC<Props> = ({ open, meterId, tracks, onClose, onChanged, onViewHistory }) => {
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();
  const mayAcknowledge = can('run_maintenance');

  const selectable = tracks.filter(t => t.health !== 'red' && !t.acknowledged);
  const blocked = tracks.filter(t => t.health === 'red');
  const already = tracks.filter(t => t.acknowledged);

  const [selected, setSelected] = useState<string[]>([]);
  const [reasonCode, setReasonCode] = useState('prove_scheduled');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(selectable.map(t => t.meter_product_id));   // default: all acknowledgeable tracks
      setReasonCode('prove_scheduled'); setNote(''); setError(null); setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meterId, tracks.length]);

  const toggle = (id: string) =>
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const res = await fetch(`${API}/meters/${encodeURIComponent(meterId)}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracks: selected, reason_code: reasonCode, note }),
      });
      const data = await res.json();
      if (!res.ok && !(data.acknowledged || []).length) {
        throw new Error(data.error || data.message || `Could not acknowledge (${res.status})`);
      }
      // Partial results are shown, never swallowed: "2 of 3" must be visible.
      const ok = (data.acknowledged || []).length;
      const skipped = data.skipped || [];
      setResult(`${ok} acknowledged` + (skipped.length ? `, ${skipped.length} skipped` : ''));
      if (skipped.length) {
        setError(skipped.map((s: any) => `${s.track}: ${s.reason}`).join(' · '));
      }
      onChanged();
      if (!skipped.length) setTimeout(onClose, 900);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: ACK_DIALOG_Z }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmber color="warning" /> Acknowledge warnings — {meterId}
      </DialogTitle>
      <DialogContent dividers>
        {!mayAcknowledge && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Acknowledging a warning requires maintenance permissions. You can still review the warnings below.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          An acknowledged warning shows as healthy with a marker, and returns automatically after 30 days,
          on the next proving, after maintenance, or if the reason changes.
        </Typography>

        {busy && <LinearProgress sx={{ mb: 1 }} />}
        {result && <Alert severity="success" sx={{ mb: 1 }}>{result}</Alert>}
        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

        {selectable.length > 1 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.length === selectable.length}
                indeterminate={selected.length > 0 && selected.length < selectable.length}
                onChange={e => setSelected(e.target.checked ? selectable.map(t => t.meter_product_id) : [])}
              />
            }
            label={<strong>Select all {selectable.length} warning tracks</strong>}
          />
        )}

        {selectable.map(t => (
          <Box key={t.meter_product_id} sx={{ pl: selectable.length > 1 ? 2 : 0 }}>
            <FormControlLabel
              control={<Checkbox checked={selected.includes(t.meter_product_id)}
                                 onChange={() => toggle(t.meter_product_id)} disabled={!mayAcknowledge} />}
              label={
                <Box>
                  <Typography variant="body2">
                    {t.label}
                    {onViewHistory && (
                      <Button size="small" startIcon={<HistoryToggleOff sx={{ fontSize: '0.9rem' }} />}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewHistory(t.meter_product_id); }}
                              sx={{ ml: 0.5, minWidth: 0, py: 0, fontSize: '0.68rem' }}>
                        History
                      </Button>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{t.reasons.join(' · ')}</Typography>
                </Box>
              }
            />
          </Box>
        ))}

        {blocked.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {blocked.map(t => (
              <Box key={t.meter_product_id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, opacity: 0.75 }}>
                <Block fontSize="small" color="error" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="body2">{t.label} <Chip size="small" color="error" label="Critical" sx={{ ml: 0.5 }} /></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Critical meters cannot be acknowledged — resolve with maintenance or a new proving. {t.reasons.join(' · ')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}

        {already.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {already.length} track{already.length > 1 ? 's are' : ' is'} already acknowledged.
            </Typography>
            {onViewHistory && already.map(t => (
              <Button key={t.meter_product_id} size="small"
                      startIcon={<HistoryToggleOff sx={{ fontSize: '0.9rem' }} />}
                      onClick={() => onViewHistory(t.meter_product_id)}
                      sx={{ py: 0, fontSize: '0.68rem' }}>
                {t.label} history
              </Button>
            ))}
          </Box>
        )}

        {mayAcknowledge && selectable.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* The dialog sits at zIndex 2200 (above the meter grid), but a Select's menu is its OWN
                popover at MUI's default 1300 -- so without this it opens BEHIND the dialog and the
                options are unreadable. Must stay above the dialog's z-index. */}
            <TextField select size="small" label="Reason" value={reasonCode} required
                       SelectProps={{ MenuProps: { sx: { zIndex: ACK_DIALOG_Z + 100 } } }}
                       onChange={e => setReasonCode(e.target.value)}>
              {REASON_CODES.map(r => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Note (optional)" value={note} multiline minRows={2}
                       placeholder="e.g. prove booked for 3 Oct"
                       onChange={e => setNote(e.target.value.slice(0, 500))} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!mayAcknowledge || busy || !selected.length} onClick={submit}>
          Acknowledge {selected.length > 0 ? `(${selected.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcknowledgeWarningPanel;
